import { computeSessionMoney, type MoneyConfig } from "./money/session-money";

export interface ClientPriceBreakdown {
  /** Cijena usluge bez PDV-a, ono što terapeut upisuje (osnovica platforme). */
  basePrice: number;
  /** Provizija Psihotrona, feePercent od basePrice. */
  platformFee: number;
  /** Koliko ostaje terapeutu: basePrice - platformFee (isto kao computeSessionMoney.therapistNet). */
  therapistAmount: number;
  vat: number;
  /** Bruto, sa PDV-om. Ovo se sprema u services.price. */
  clientPrice: number;
}

/**
 * Poreski profil terapeuta utiče samo na ulazni PDV (`therapistVat`, `payoutGross`), ne
 * na `platformBase`, `platformFee` ni `therapistNet`. Za prikaz kalkulatora je zato
 * svejedno koji se profil preda; ovaj je samo fiksiran da poziv bude deterministički.
 */
const DISPLAY_TAX_PROFILE = { payoutMethod: "invoice", isVatRegistered: false } as const;

/**
 * Smjer naprijed: terapeut upisuje cijenu usluge bez PDV-a (osnovicu platforme), a
 * izvodimo bruto cijenu za klijenta: osnovica + PDV. Koriste je forma usluge i
 * onboarding. Provizija i terapeutov dio se NE računaju ovdje nego se čitaju iz
 * `computeSessionMoney(clientPrice)`, jer je to jedino mjesto gdje se dijeli osnovica
 * (CLAUDE.md, "Novac, PDV i provizija"). Tako terapeut u kalkulatoru vidi tačno onaj
 * iznos koji će se snimiti na seansu pri bookingu. `computeSessionMoney` iz bruto
 * cijene vraća istu osnovicu koju je terapeut upisao za svaku vrijednost u parama,
 * jer je |bruto / (1 + PDV) - osnovica| uvijek manje od pola pare.
 */
export function computeClientPriceBreakdown(
  basePrice: number | null,
  config: MoneyConfig
): ClientPriceBreakdown {
  const base = round2(Math.max(0, basePrice ?? 0));
  const vat = round2(base * config.vatRate);
  const clientPrice = round2(base + vat);
  const money = computeSessionMoney(clientPrice, config, DISPLAY_TAX_PROFILE);

  return {
    basePrice: base,
    platformFee: money.platformFee,
    therapistAmount: money.therapistNet,
    vat,
    clientPrice,
  };
}

/**
 * Smjer nazad: već poznata bruto cijena (spremljena, ili grupna terapija gdje se upisuje
 * bruto) razložena nadolje kroz `computeSessionMoney`, bez izvođenja bruta iz osnovice.
 * Tako prikaz za spremljenu cijenu pokazuje tačno taj bruto, i kad ga kalkulator iz
 * "osnovica + PDV" ne bi proizveo (16.00 se ne može dobiti iz cijene u parama).
 */
export function computeGrossPriceBreakdown(
  clientPrice: number | null,
  config: MoneyConfig
): ClientPriceBreakdown {
  const money = computeSessionMoney(clientPrice ?? 0, config, DISPLAY_TAX_PROFILE);
  return {
    basePrice: money.platformBase,
    platformFee: money.platformFee,
    therapistAmount: money.therapistNet,
    vat: money.vat,
    clientPrice: money.clientTotal,
  };
}

/**
 * Bruto -> cijena bez PDV-a, za učitavanje spremljene `services.price` u polje koje
 * terapeut uređuje. Delegira na `computeSessionMoney` (`platformBase`), da ostane jedna
 * formula: osnovica koju forma prikaže je ista osnovica na koju se kod bookinga obračuna
 * provizija.
 */
export function basePriceFromClientPrice(
  clientPrice: number | null,
  config: MoneyConfig
): number | null {
  if (clientPrice == null) return null;
  return computeSessionMoney(clientPrice, config, DISPLAY_TAX_PROFILE).platformBase;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
